/**
 * Session-slot x axis: every RTH session is 390 slots wide and sessions sit
 * edge to edge, so overnight takes no space but a missing minute keeps its
 * width. A plain time scale spends ~70% of a two-day panel on the overnight
 * gap; an index scale draws straight through VIX's missing minutes.
 */

import type { BreadthPoint } from '../../services/zerodayBreadthService';

export const SESSION_SLOTS = 390;
const RTH_OPEN = 9 * 60 + 30;

const etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

interface EtClock {
  day: string;
  minutes: number;
}

function etClock(seconds: number): EtClock {
  const bag: Record<string, string> = {};
  for (const part of etFormatter.formatToParts(new Date(seconds * 1000))) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  const hour = Number(bag.hour) % 24;
  return { day: `${bag.year}-${bag.month}-${bag.day}`, minutes: hour * 60 + Number(bag.minute) };
}

export interface SlotPoint extends BreadthPoint {
  slot: number;
}

export interface SessionRun {
  /** ET date, `YYYY-MM-DD`. */
  day: string;
  /** Slot of this session's 09:30. */
  start: number;
  points: SlotPoint[];
}

/** ET session dates present in any series, oldest first. */
export function sessionDays(series: readonly (readonly BreadthPoint[])[]): string[] {
  const days = new Set<string>();
  for (const points of series) {
    for (const run of toSessionRuns(points)) days.add(run.day);
  }
  return [...days].sort();
}

/**
 * Groups ascending RTH points into sessions laid edge to edge.
 * `days` pins each date to a shared index so two series stay aligned when
 * one of them skips a session.
 */
export function toSessionRuns(points: readonly BreadthPoint[], days?: readonly string[]): SessionRun[] {
  const runs: SessionRun[] = [];
  const byDay = new Map<string, SessionRun>();
  for (const point of points) {
    const clock = etClock(point.time);
    const offset = clock.minutes - RTH_OPEN;
    if (offset < 0 || offset >= SESSION_SLOTS) continue;
    let run = byDay.get(clock.day);
    if (!run) {
      const index = days ? days.indexOf(clock.day) : runs.length;
      if (index < 0) continue;
      run = { day: clock.day, start: index * SESSION_SLOTS, points: [] };
      byDay.set(clock.day, run);
      runs.push(run);
    }
    run.points.push({ ...point, slot: run.start + offset });
  }
  return runs;
}

/** Slot of a given ET wall-clock minute inside a run, e.g. 12:00 → start + 150. */
export function slotAt(run: SessionRun, hour: number, minute: number): number {
  return run.start + hour * 60 + minute - RTH_OPEN;
}
